import moment from "moment";
import { parseBooleanVal } from "Utils/utils";
import {
  applyParticipantTravelToSplits,
  claimTypeFromIsTravel,
  createEmptyClientSplit,
  createWorkerCarerRow,
  mapCommonWorkerToOption,
  mapSilHomeParticipantToOption,
  normalizeAdvanceShiftTaskForForm,
  resolveKmForInvoicingValue,
  resolveStaValue,
} from "./advanceModeSplitDefaults";

export { isAdvanceMultiAssignmentShift } from "../../Calendar/advanceShiftCalendarUtils";

const DATETIME_FORMATS = ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601];

const parseDateTime = (value) => {
  if (!value) return null;
  const strict = moment(value, DATETIME_FORMATS, true);
  if (strict.isValid()) return strict;
  const loose = moment(value);
  return loose.isValid() ? loose : null;
};

/** Wall-clock "HH:mm" for shift / segment time fields (no timezone conversion). */
const toClockTime = (value) => {
  const parsed = parseDateTime(value);
  return parsed ? parsed.format("HH:mm") : "";
};

/**
 * Wall-clock "HH:mm" that preserves the value's own offset instead of converting
 * to the browser's local zone. Worker-assignment times come back as cast
 * `datetime` attributes (ISO 8601 with a trailing Z/offset), unlike shift and
 * split times which are raw "YYYY-MM-DD HH:mm:ss" strings. Using parseZone keeps
 * the stored wall-clock (e.g. 09:00 stays 09:00) rather than shifting it.
 */
const toWallClockTime = (value) => {
  if (!value) return "";
  const parsed = moment.parseZone(value);
  return parsed.isValid() ? parsed.format("HH:mm") : "";
};

/** Normalised "YYYY-MM-DD" shift date. */
const toShiftDate = (value) => {
  if (!value) return "";
  const parsed = moment(
    value,
    ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601],
    true
  );
  if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  const loose = moment(value);
  return loose.isValid() ? loose.format("YYYY-MM-DD") : "";
};

/**
 * Pull the V5 shift object out of whatever envelope the GET endpoint returned.
 * The API responds as { data: <shift> } (response()->success('data', $shift)),
 * but we also tolerate a raw shift or an extra nesting level.
 */
export const unwrapAdvanceShiftPayload = (response) => {
  if (!response || typeof response !== "object") return null;

  const looksLikeShift = (obj) =>
    obj &&
    typeof obj === "object" &&
    (obj.is_multi_assignment != null ||
      obj.worker_assignments != null ||
      obj.workerAssignments != null ||
      obj.split_shifts != null ||
      obj.splitShifts != null ||
      obj.uuid != null);

  if (looksLikeShift(response) && response.data == null) {
    return response;
  }

  const inner = response.data;
  if (looksLikeShift(inner)) return inner;
  if (inner && typeof inner === "object" && looksLikeShift(inner.data)) {
    return inner.data;
  }
  // Nothing resembled a shift (e.g. an error envelope) — signal a fallback.
  return null;
};

const parseShiftLocation = (raw) => {
  if (!raw) return null;
  let location = raw;
  if (typeof location === "string") {
    try {
      location = JSON.parse(location);
    } catch (e) {
      return null;
    }
  }
  if (!location || typeof location !== "object") return null;
  const street = location.street || location.street_address || "";
  const suburb = location.suburb || "";
  if (!street && !suburb) return null;
  return {
    street,
    suburb,
    state: location.state ?? null,
    state_name: location.state_name || "",
    postcode: location.postcode || "",
    lat: location.lat ?? null,
    lng: location.lng ?? null,
  };
};

const dedupeById = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = item?.id ?? item?.uuid;
    if (key == null || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const mapParticipant = (participant) =>
  mapSilHomeParticipantToOption(participant);

const mapWorker = (worker) =>
  mapCommonWorkerToOption({
    ...worker,
    profile_picture: worker?.profile_picture ?? worker?.photo ?? null,
  });

/**
 * Parse a V5 GET /shift/{uuid} response into the state needed to drive advance
 * mode in the WorkerShift form. Returns plain data only — the caller applies it
 * via setValue / field-array replace / local state setters.
 *
 * @param {object} shift  Unwrapped V5 shift object
 * @param {{ settingsDefaultKmCode?: string|null }} [options]
 */
export const parseAdvanceShiftEditResponse = (shift, options = {}) => {
  if (!shift || typeof shift !== "object") return null;

  const settingsDefaultKmCode =
    options.settingsDefaultKmCode !== undefined
      ? options.settingsDefaultKmCode
      : null;

  const sta = resolveStaValue(shift.is_sta ?? shift.sta);
  const isOpenShift = parseBooleanVal(shift.is_open_shift);

  const splitShifts = shift.split_shifts ?? shift.splitShifts ?? [];
  const assignments =
    shift.worker_assignments ?? shift.workerAssignments ?? [];

  // ---- Participant billing segments → advance_splits ----------------------
  const mappedSplits = (Array.isArray(splitShifts) ? splitShifts : []).map(
    (split) => {
      const participants = dedupeById(
        (split.participants || []).map(mapParticipant).filter(Boolean)
      );
      const base = createEmptyClientSplit(participants[0] || null, {});
      return {
        ...base,
        participant: participants[0] || null,
        participants,
        participant_start_time: toClockTime(split.start_time),
        participant_end_time: toClockTime(split.end_time),
        invoice_service_type_id: split.service_type_id ?? null,
        service_type_name:
          split.service_type_name ??
          split.invoice_service_type?.name ??
          null,
        service_type_funding_type:
          split.service_type_funding_type ??
          split.invoice_service_type?.funding_type ??
          null,
        invoice_item_id: split.invoice_item_id ?? null,
        custom_line_item_uuid:
          split.custom_line_item_uuid ??
          split.custom_line_item?.uuid ??
          null,
        support_item_number:
          split.ndis_support_item_number || split.support_item_number || "",
        support_item_name:
          split.ndis_support_item_name ||
          split.support_item_name ||
          split.custom_line_item?.description ||
          "",
        ratio: split.ratio || "1:1",
        is_sleepover: parseBooleanVal(split.is_sleepover),
        km_for_invoicing: resolveKmForInvoicingValue(
          split.km_for_invoicing ?? split.apply_km
        ),
        default_km_code:
          split.default_km_code !== undefined
            ? split.default_km_code
            : settingsDefaultKmCode,
        // V5 persists claim_type directly; fall back to is_travel only for any
        // legacy rows that stored the boolean instead of the label.
        claim_type:
          split.claim_type ??
          (split.is_travel != null
            ? claimTypeFromIsTravel(split.is_travel)
            : null),
        sta,
      };
    }
  );

  // Prefer participant_travel (form or response) over per-segment flags.
  const participantTravel =
    shift.form?.participant_travel ??
    shift.participant_travel ??
    [];
  const advance_splits = applyParticipantTravelToSplits(
    mappedSplits,
    participantTravel
  );

  // ---- Selected participants (union across all segments) -------------------
  const advance_selected_participants = dedupeById(
    advance_splits.flatMap((split) => split.participants || [])
  );

  // ---- Worker assignments → advance_carers --------------------------------
  const assignmentSource =
    (Array.isArray(assignments) && assignments.length > 0
      ? assignments
      : null) ??
    (Array.isArray(shift.workers) ? shift.workers : []);
  const calendarWorkers = Array.isArray(shift.workers) ? shift.workers : [];

  const findCalendarWorker = (assignment) => {
    const assignmentId = assignment.assignment_id ?? assignment.id;
    const workerId = assignment.worker_id ?? assignment.worker?.id;
    return (
      calendarWorkers.find(
        (entry) =>
          assignmentId != null &&
          String(entry.assignment_id ?? entry.id) === String(assignmentId)
      ) ||
      calendarWorkers.find(
        (entry) =>
          workerId != null &&
          String(entry.worker_id ?? entry.worker?.id) === String(workerId)
      ) ||
      null
    );
  };

  const advance_carers = assignmentSource.map((assignment) => {
    const worker = assignment.worker ? mapWorker(assignment.worker) : null;
    const calendarEntry = findCalendarWorker(assignment);
    const actualStart = toWallClockTime(
      assignment.worker_start_time ?? assignment.actual_start_time ?? null
    );
    const actualEnd = toWallClockTime(
      assignment.worker_end_time ?? assignment.actual_end_time ?? null
    );
    return {
      worker,
      is_open_slot:
        !worker ||
        parseBooleanVal(assignment.is_open_slot ?? assignment.is_open),
      // Editable schedule window for this assignment
      worker_start_time: toWallClockTime(
        assignment.start_time ?? shift.start_time
      ),
      worker_end_time: toWallClockTime(assignment.end_time ?? shift.end_time),
      // Clocked / timesheet actual times (read-only in the worker card)
      actual_start_time: actualStart || "",
      actual_end_time: actualEnd || "",
      sleepover: parseBooleanVal(
        assignment.sleepover ?? assignment.is_sleepover ?? false
      ),
      active_hours: assignment.active_hours ?? assignment.activeHours ?? "",
      assignment_id: assignment.assignment_id ?? assignment.id ?? null,
      status: assignment.status ?? calendarEntry?.status ?? null,
      progress:
        assignment.progress ??
        calendarEntry?.progress ??
        assignment.timesheet?.progress ??
        null,
      shift_accept:
        assignment.shift_accept ??
        assignment.shiftAccept ??
        calendarEntry?.shift_accept ??
        null,
    };
  });

  const advance_selected_workers = dedupeById(
    advance_carers.map((row) => row.worker).filter(Boolean)
  );

  // ---- Tasks & notes ------------------------------------------------------
  // `form.shift_tasks` collapses the per-worker rows back into one logical task
  // carrying assign_to + ids. The raw `shift_tasks` relation is one row PER worker,
  // so reading it would show an "all workers" task once per assigned worker.
  const formTasks = shift.form?.shift_tasks ?? shift.form?.shiftTasks;
  const rawTasks = Array.isArray(formTasks)
    ? formTasks
    : Array.isArray(shift.shift_tasks ?? shift.shiftTasks)
    ? shift.shift_tasks ?? shift.shiftTasks
    : [];
  const tasks = rawTasks.map((task) => normalizeAdvanceShiftTaskForForm(task));

  // V5 advance show returns worker-grouped timesheet notes (read-only).
  // Legacy flat { pt_id, first_name, notes } rows remain editable `notes`.
  let notes = [];
  let shiftNotes = [];
  if (Array.isArray(shift.shift_notes)) {
    const workerGrouped = [];
    const flat = [];
    shift.shift_notes.forEach((item) => {
      if (isAdvanceWorkerShiftNotesGroup(item)) {
        workerGrouped.push(item);
      } else {
        flat.push(item);
      }
    });
    shiftNotes = workerGrouped;
    notes = flat;
  } else if (shift.note) {
    notes = [{ notes: shift.note }];
  }

  const silHomeId =
    shift.sil_home_id ??
    shift.sil_home?.id ??
    shift.form?.sil_home_id ??
    null;

  return {
    // basic / shared fields
    shift_date: toShiftDate(shift.shift_date),
    start_time: toClockTime(shift.start_time),
    end_time: toClockTime(shift.end_time),
    location_tracking: parseBooleanVal(shift.location_tracking),
    is_signature: parseBooleanVal(shift.is_signature),
    is_radius: parseBooleanVal(shift.is_radius),
    sleepover: parseBooleanVal(shift.sleepover ?? shift.is_sleepover ?? false),
    sta,
    shift_instruction: shift.shift_instruction || "",
    shift_location: parseShiftLocation(shift.shift_location),

    // advance-mode fields
    advance_open_shift: isOpenShift,
    advance_selected_participants,
    advance_selected_workers,
    advance_splits,
    advance_carers,

    // SIL home (only the id is returned by the show endpoint)
    advance_sil_home_enabled: silHomeId != null,
    advance_sil_home_id: silHomeId,
    advance_sil_home_uuid: shift.sil_home?.uuid ?? null,
    advance_sil_home_label: shift.sil_home?.label ?? null,

    // local component state
    tasks,
    notes,
    /** Per-worker timesheet notes from V5 show — read-only on shift form. */
    shiftNotes,
  };
};

/**
 * V5 advance shift_notes entry: one timesheet per worker with nested participant notes.
 * Distinct from legacy flat { pt_id, first_name, last_name, notes } rows.
 */
export const isAdvanceWorkerShiftNotesGroup = (item) =>
  item != null &&
  typeof item === "object" &&
  Array.isArray(item.notes) &&
  (item.worker != null ||
    item.worker_id != null ||
    item.timesheet_id != null ||
    item.timesheet_uuid != null);
